/**
 * New-version flow (owner's rule 2026-09-19): never reload under the user's feet. When the server has a newer build,
 * a prompt invites them to tap and install it; the tap drops the old service worker and caches and reloads once.
 */
import { useSyncExternalStore } from 'react'

let available: string | null = null
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())

export function markUpdateAvailable(build: string) {
  if (available === build) return
  available = build
  emit()
}

/** Reads /version.json; returns the newer build id when the server's build differs from this shell's. */
export async function checkForUpdate(current: string, fetcher: typeof fetch = fetch): Promise<string | null> {
  try {
    const res = await fetcher(`/version.json?t=${Date.now()}`, { cache: 'no-store', credentials: 'omit' })
    if (!res.ok) return null
    const { build } = (await res.json()) as { build?: string }
    if (!build || build === current) return null
    markUpdateAvailable(build)
    return build
  } catch {
    return null // offline or a proxy hiccup: the next check tries again
  }
}

/** The tap: new service worker, fresh caches, one reload. */
export async function applyUpdate() {
  try {
    if ('serviceWorker' in navigator) for (const r of await navigator.serviceWorker.getRegistrations()) await r.unregister().catch(() => undefined)
    if ('caches' in window) for (const k of await caches.keys()) await caches.delete(k).catch(() => undefined)
  } finally {
    location.reload()
  }
}

export function useUpdateAvailable() {
  return useSyncExternalStore((cb) => (listeners.add(cb), () => listeners.delete(cb)), () => available, () => null)
}
